import { useEffect, useState } from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";

import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

const CustomList = ({
  isLoading,
  items,
  selectItem,
  type,
  height = 300,
}: {
  isLoading?: boolean;
  items: { value: string; selected: boolean; default?: boolean }[];
  selectItem: (value: string) => void;
  type: "selected" | "unselected";
  height?: number;
}) => {
  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        style={{ minHeight: height }}
        height={height}
        width="100%"
      />
    );
  }

  return (
    <Paper sx={{ width: "100%", height, overflow: "auto", flexGrow: 1 }}>
      <List dense component="div" role="list">
        {items.map(
          (item: { value: string; selected: boolean; default?: boolean }) => {
            const labelId = `transfer-list-item-${item.value}-label`;

            return (
              <ListItemButton
                key={item.value}
                role="listitem"
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  cursor: "default",
                }}
              >
                {type === "selected" && (
                  <IconButton
                    // color="primary"
                    style={{
                      backgroundColor: "#005CA6",
                      color: "white",
                      height: 24,
                      width: 24,
                      borderRadius: 4,
                    }}
                    size="small"
                    onClick={() => selectItem(item.value)}
                  >
                    <KeyboardDoubleArrowLeftIcon />
                  </IconButton>
                )}
                <ListItemText
                  id={labelId}
                  primary={item.value}
                  style={{ flexGrow: 1 }}
                />

                {type === "unselected" && (
                  <IconButton
                    style={{
                      backgroundColor: "#005CA6",
                      color: "white",
                      height: 24,
                      width: 24,
                      borderRadius: 4,
                    }}
                    size="small"
                    onClick={() => selectItem(item.value)}
                  >
                    <KeyboardDoubleArrowRightIcon />
                  </IconButton>
                )}
              </ListItemButton>
            );
          },
        )}
      </List>
    </Paper>
  );
};

const TransferList = ({
  isLoading,
  records,
  setRecords,
  filter,
  onSelect,
  height,
  settings,
}: {
  isLoading?: boolean;
  records: { value: string; selected: boolean; default?: boolean }[];
  setRecords: (
    records: {
      value: string;
      selected: boolean;
      default?: boolean;
      manuallyCreated?: boolean;
    }[],
  ) => void;

  filter?: string;
  onSelect?: (value: string, selected: boolean) => void;
  height?: number;
  settings?: {
    unselectedTitle: string;
    selectedTitle: string;
    defaultTitle: string;
  };
}) => {
  const select = (value: string) => {
    const newRecords = [...records];

    const i = newRecords.findIndex((r) => r.value === value);
    newRecords[i].selected = true;
    if (newRecords.filter((r) => r.selected).length === 1)
      newRecords[i].default = true;

    if (onSelect) onSelect(value, true);
    setRecords(newRecords);
  };

  const deselect = (value: string) => {
    const newRecords = [...records];

    const i = newRecords.findIndex((r) => r.value === value);
    newRecords[i].selected = false;
    newRecords[i].default = false;

    if (newRecords.filter((r) => r.default).length === 0) {
      const firstSelected = newRecords.findIndex((r) => r.selected);
      if (firstSelected !== -1) {
        newRecords[firstSelected].default = true;
      }
    }

    if (onSelect) onSelect(value, false);
    setRecords(newRecords);
  };

  const [filteredRecords, setFilteredRecords] = useState(records);
  useEffect(() => {
    if (!filter) {
      setFilteredRecords(records);
    } else {
      setFilteredRecords(
        records.filter((r) =>
          r.value.toUpperCase().includes(filter.toUpperCase()),
        ),
      );
    }
  }, [records, filter]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        width: "100%",
      }}
    >
      <div style={{ display: "flex", gap: 15 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            width: "50%",
          }}
        >
          <div
            style={{
              textAlign: "center",
              fontSize: 12,
              fontWeight: 700,
              color: "#797979",
            }}
          >
            {settings?.unselectedTitle ? settings.unselectedTitle : "Others"}
          </div>
          <CustomList
            isLoading={isLoading}
            items={filteredRecords.filter((r) => !r.selected)}
            selectItem={select}
            type="unselected"
            height={height}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            width: "50%",
          }}
        >
          <div
            style={{
              textAlign: "center",
              fontSize: 12,
              fontWeight: 700,
              color: "#797979",
            }}
          >
            {settings?.selectedTitle ? settings.selectedTitle : "Favorites"}
          </div>
          <CustomList
            isLoading={isLoading}
            items={filteredRecords.filter((r) => r.selected)}
            selectItem={deselect}
            type="selected"
            height={height}
          />
        </div>
      </div>
    </div>
  );
};

export default TransferList;
